import {
  FC,
  MutableRefObject,
  ReactNode,
  createContext,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import useAutoFocus from 'src/hooks/useAutoFocus';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import useGuideMe from 'src/hooks/useGuideMe';
import useRedirect from 'src/hooks/useRedirect';
import useStep from 'src/hooks/useStep';
import { IDisableStep, IModalData } from 'src/pages/EnterReceipts';
import {
  ClosedInvoice,
  ClosedPo,
  ClosedPoWithoutReview,
  GuideMeSteps,
} from 'src/sections/enter_invoice/GuideMeSteps';
import useCompanyInvoiceScansApi from 'src/services/useCompanyInvoiceScansApi';
import useCreateOcrDocumentApi from 'src/services/useCreateOcrDocumentApi';
import useIsExistingOcrDocumentApi from 'src/services/useIsExistingOcrDocumentApi';
import { Company } from 'src/types/Companies';
import { IInvoiceDocument } from 'src/types/InvoiceDocument';
import { IOfflineSupplier } from 'src/types/OfflineSupplier';
import { IPO } from 'src/types/PO';
import SearchPO from 'src/types/SearchPO';
import { SetState } from 'src/types/generic';
interface IEnterInvoiceContext {
  selectedCompany: Company;
  currentStep: number;
  nextStep: () => void;
  goToPrevStep: () => void;
  setStep: SetState<number>;
  invoiceNumber: SearchPO;
  setInvoiceNumber: SetState<SearchPO>;
  searchPo: SearchPO;
  setSearchPo: SetState<SearchPO>;
  supplierSearch: string;
  setSupplierSearch: SetState<string>;
  lineItemSearch: string;
  setLineItemSearch: SetState<string>;
  selectedInvoicePoRow: IPO | null;
  setSelectedInvoicePoRow: SetState<IPO | null>;
  selectedInvoiceRow: IInvoiceDocument | null;
  setSelectedInvoiceRow: SetState<IInvoiceDocument | null>;
  disableStep: IDisableStep;
  setDisableStep: SetState<IDisableStep>;
  setGuideMeModalData: SetState<IModalData[]>;
  openCreatePoModal: boolean;
  toggleOpenCreatePoModal: () => void;
  openCreateInvoiceModal: boolean;
  toggleOpenCreateInvoiceModal: () => void;
  showPreview: boolean;
  setShowPreview: SetState<boolean>;
  alert: boolean;
  setAlert: SetState<boolean>;
  guideMe: JSX.Element;
  openGuideMe: () => void;
  selectedSupplierInvoiceRow: IOfflineSupplier;
  setSelectedSupplierInvoiceRow: SetState<IOfflineSupplier>;
  buttonRef: MutableRefObject<HTMLButtonElement | null>;
  isOcrRedirect: boolean;
  setIsOcrRedirect: SetState<boolean>;
  attachId: string;
  setAttachId: SetState<string>;
  isExistingOcrCallLoading: boolean;
  handleOcrInvoice: (supplierId: number, openAttachmentModal: () => void) => Promise<void>;
  isOcrCompany: boolean;
}
export const EnterInvoiceContext = createContext<IEnterInvoiceContext>({} as IEnterInvoiceContext);

interface EnterInvoiceProviderProps {
  children: ReactNode;
}

const EnterInvoiceContextProvider: FC<EnterInvoiceProviderProps> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const { confirm } = useConfirm();
  const poNumber = useMemo(() => searchParams.get('poNumber') || '', [searchParams]);
  const docNumber = useMemo(() => searchParams.get('docNumber') || '', [searchParams]);
  const [isOcrRedirect, setIsOcrRedirect] = useState(!!searchParams.get('isOcrRedirect') || false);
  const [attachId, setAttachId] = useState(searchParams.get('attachId') || '');
  const [currentStep, helpers] = useStep(5, poNumber ? 4 : docNumber ? 2 : 1);
  const { goToNextStep, goToPrevStep, setStep } = helpers;
  const [invoiceNumber, setInvoiceNumber] = useState<SearchPO>(
    docNumber
      ? {
          value: docNumber,
          disable: true,
        }
      : {
          value: '',
          disable: false,
        }
  );
  const [searchPo, setSearchPo] = useState<SearchPO>(
    poNumber
      ? { value: poNumber, disable: true }
      : {
          value: '',
          disable: false,
        }
  );
  const [supplierSearch, setSupplierSearch] = useState<string>('');
  const [lineItemSearch, setLineItemSearch] = useState<string>('');
  const [showPreview, setShowPreview] = useState(isOcrRedirect ? true : false);
  const [alert, setAlert] = useState(false);
  const { user } = useAuth();
  const { company: selectedCompany } = useCompany();
  const [selectedInvoicePoRow, setSelectedInvoicePoRow] = useState<IPO | null>(null);
  const [selectedInvoiceRow, setSelectedInvoiceRow] = useState<IInvoiceDocument | null>(null);
  const [disableStep, setDisableStep] = useState<IDisableStep>({
    disableNext: false,
    disablePrev: false,
  });
  const buttonRef = useAutoFocus(true, [
    currentStep,
    selectedInvoiceRow?.selected,
    selectedInvoicePoRow?.selected,
  ]);
  const guideId = `enterInvoiceGuide-${user.ClientId}-step-${currentStep}`;
  const [guideMeModalData, setGuideMeModalData] = useState<IModalData[]>(GuideMeSteps);
  const [openCreatePoModal, setOpenCreatePoModal] = useState<boolean>(false);
  const [openCreateInvoiceModal, setOpenCreateInvoiceModal] = useState<boolean>(false);
  const { guideMe, openGuideMe } = useGuideMe({
    steps: guideMeModalData,
    guideId: guideId,
    focusRef: buttonRef,
  });
  const [selectedSupplierInvoiceRow, setSelectedSupplierInvoiceRow] = useState<IOfflineSupplier>(
    {} as IOfflineSupplier
  );

  const { checkIsExistingOcrDocument, isExistingOcrCallLoading } = useIsExistingOcrDocumentApi();
  const { companyInvoiceScans } = useCompanyInvoiceScansApi();
  const { createOcrDocument } = useCreateOcrDocumentApi();

  const isOcrCompany = useMemo(
    () =>
      !!companyInvoiceScans.find(
        (company) => company.companyId === Number(selectedCompany.companyId)
      )?.usesOcr,
    [companyInvoiceScans, selectedCompany?.companyId]
  );

  const toggleOpenCreateInvoiceModal = useCallback(
    () => setOpenCreateInvoiceModal((prev) => !prev),
    [setOpenCreateInvoiceModal]
  );

  const toggleOpenCreatePoModal = useCallback(
    () => setOpenCreatePoModal((prev) => !prev),
    [setOpenCreatePoModal]
  );
  const { getUrl, redirect } = useRedirect();
  const redirectToInvoiceDetails = useCallback(() => {
    redirect(
      getUrl('invoiceDetails')(
        String(selectedInvoiceRow?.receiptId),
        String(selectedInvoiceRow?.poId)
      )
    );
  }, [selectedInvoiceRow?.receiptId, selectedInvoiceRow?.poId, redirect, getUrl]);

  const handleClosePo = useCallback(async () => {
    const invoiceId = selectedInvoiceRow?.receiptId;
    await confirm({
      ...(invoiceId ? ClosedInvoice : isOcrCompany ? ClosedPoWithoutReview : ClosedPo),
      cancelButtonText: invoiceId ? 'Cancel' : 'OK',
      confirmButtonText: invoiceId ? 'OK' : '',
      hasNoConfirmButton: invoiceId ? false : true,
      width: '500px',
    }).then((isConfirm) => {
      if (isConfirm && invoiceId) {
        redirectToInvoiceDetails();
      }
    });
  }, [confirm, isOcrCompany, redirectToInvoiceDetails, selectedInvoiceRow?.receiptId]);

  const handleEDI = useCallback(async () => {
    await confirm({
      title: 'EDI 810 Enabled',
      content: 'There may be a pending transmission and a duplicate record may be created',
      cancelButtonText: 'Cancel',
      cancelColor: 'info',
      confirmButtonText: 'Continue',
      width: '500px',
    }).then((isConfirm) => {
      if (isConfirm) {
        toggleOpenCreateInvoiceModal();
      }
    });
  }, [confirm, toggleOpenCreateInvoiceModal]);

  const getExistingOcrDocument = useCallback(
    (supplierId: number) =>
      checkIsExistingOcrDocument({
        companyId: selectedCompany?.companyId,
        invoiceNumber: invoiceNumber.value,
        supplierId,
      }),
    [checkIsExistingOcrDocument, invoiceNumber.value, selectedCompany?.companyId]
  );

  const handleOcrInvoice = useCallback(
    async (supplierId: number, openAttachmentModal: () => void) => {
      if (selectedInvoiceRow?.selected) {
        if (selectedInvoiceRow.ocrGenerated) {
          redirect(
            getUrl('invoiceDetails')(
              String(selectedInvoiceRow.receiptId),
              String(selectedInvoiceRow.poId)
            ),
            false,
            false
          );
        } else {
          openAttachmentModal();
        }
      } else if (isOcrRedirect && attachId) {
        const isExistingOcrDocument =
          invoiceNumber.value && (await getExistingOcrDocument(supplierId));

        if (isExistingOcrDocument) {
          await confirm({
            title: 'Existing OCR Document',
            content:
              'The document# provided is already in use for an OCR generated document for this supplier and company. You must verify this is not a duplicate document or modify the document# to proceed.',
            cancelButtonText: 'Ok',
            cancelColor: 'info',
            hasNoConfirmButton: true,
            width: '500px',
          });
        } else {
          try {
            const reqBody = {
              companyId: Number(selectedCompany.companyId),
              invoiceNumber: invoiceNumber.value,
              poNumber: searchPo.value,
              poId: selectedInvoicePoRow?.poId || null,
              supplierId: supplierId,
              isOnlineSupplier: false,
            };
            const res = await createOcrDocument({
              attachmentId: Number(attachId),
              body: reqBody,
            });
            redirect(
              getUrl('invoiceDetails')(String(res.invoiceId), String(res.poId)),
              false,
              true
            );
          } catch (error) {
            console.log('error', error);
          }
        }
      } else {
        openAttachmentModal();
      }
    },
    [
      attachId,
      confirm,
      createOcrDocument,
      getExistingOcrDocument,
      getUrl,
      invoiceNumber.value,
      isOcrRedirect,
      redirect,
      searchPo.value,
      selectedCompany.companyId,
      selectedInvoicePoRow?.poId,
      selectedInvoiceRow,
    ]
  );

  const nextStep = useCallback(async () => {
    const isOpenPo = selectedInvoicePoRow?.poStatus === 'Open';
    const isComplete = selectedInvoiceRow?.status === 'Completed';
    const isDelete = selectedInvoiceRow?.status === 'Deleted';
    const isEDIEnable = !!selectedInvoicePoRow?.isEDIReceipt;
    if (currentStep === 1 && !invoiceNumber.value) {
      setAlert(true);
      return;
    } else if (currentStep === 2 && selectedInvoiceRow?.selected) {
      redirectToInvoiceDetails();
      return;
    } else if (currentStep === 4 && !selectedInvoicePoRow?.selected) {
      toggleOpenCreatePoModal();
      return;
    } else if (currentStep === 5 && isOpenPo && !selectedInvoiceRow?.selected && isEDIEnable) {
      handleEDI();
      return;
    } else if (currentStep === 5 && isOpenPo) {
      if (isOcrCompany) {
        handleOcrInvoice(selectedInvoicePoRow.supplierId, toggleOpenCreateInvoiceModal);
      } else {
        toggleOpenCreateInvoiceModal();
      }
      return;
    } else if (currentStep === 5 && isDelete) {
      redirectToInvoiceDetails();
      return;
    } else if (currentStep === 5 && (!isOpenPo || isComplete)) {
      handleClosePo();
      return;
    }
    goToNextStep();
  }, [
    selectedInvoicePoRow?.poStatus,
    selectedInvoicePoRow?.isEDIReceipt,
    selectedInvoicePoRow?.selected,
    selectedInvoicePoRow?.supplierId,
    selectedInvoiceRow?.status,
    selectedInvoiceRow?.selected,
    currentStep,
    invoiceNumber.value,
    goToNextStep,
    redirectToInvoiceDetails,
    toggleOpenCreatePoModal,
    handleEDI,
    isOcrCompany,
    handleOcrInvoice,
    toggleOpenCreateInvoiceModal,
    handleClosePo,
  ]);

  return (
    <EnterInvoiceContext.Provider
      value={{
        selectedCompany,
        currentStep,
        nextStep,
        goToPrevStep,
        setStep,
        searchPo,
        setSearchPo,
        supplierSearch,
        setSupplierSearch,
        lineItemSearch,
        setLineItemSearch,
        selectedInvoicePoRow,
        setSelectedInvoicePoRow,
        openCreatePoModal,
        toggleOpenCreatePoModal,
        openCreateInvoiceModal,
        toggleOpenCreateInvoiceModal,
        alert,
        setAlert,
        selectedInvoiceRow,
        setSelectedInvoiceRow,
        invoiceNumber,
        setInvoiceNumber,
        disableStep,
        setDisableStep,
        setGuideMeModalData,
        showPreview,
        setShowPreview,
        guideMe,
        openGuideMe,
        selectedSupplierInvoiceRow,
        setSelectedSupplierInvoiceRow,
        buttonRef,
        isOcrRedirect,
        setIsOcrRedirect,
        attachId,
        setAttachId,
        isExistingOcrCallLoading,
        handleOcrInvoice,
        isOcrCompany,
      }}
    >
      {children}
    </EnterInvoiceContext.Provider>
  );
};

export default EnterInvoiceContextProvider;
